import { Box, IconButton, Typography } from '@mui/material';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { theme } from '@/styles/theme';
import { CustomAvatar } from '@/components/avatar/Avatar';
import { useTranslation } from 'react-i18next';
import { InfoBox } from '@/components/infoBox/InfoBox';
import { useMediaQuery, useTheme } from '@mui/material';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';

interface Appointment {
  id: string;
  patientName: string;
  dependent?: any;
  avatar: string;
  type: string;
  location: string;
  doctorName: string;
  date: string;
  time: string;
}

export const CardSchedule = ({
  appointment,
  onViewDetails,
}: {
  appointment: Appointment;
  onViewDetails: (appointmentId: string) => void;
}) => {
  const { t } = useTranslation('cardSchedule');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box sx={styles.card}>
      <Box sx={styles.header}>
        <CustomAvatar name={appointment.patientName} src={appointment.avatar} />
        <Box>
          <Typography sx={styles.headerTitle}>{t('patient_name')}</Typography>
          <Typography sx={styles.headerName}>
            {appointment.patientName}
          </Typography>
        </Box>
      </Box>

      <InfoBox
        iconLeft={<LocalHospitalOutlinedIcon sx={styles.iconLeft} />}
        title={t('type_attendance')}
        text={`${appointment.type}`}
      />
      <InfoBox
        iconLeft={<MedicalServicesOutlinedIcon sx={styles.iconLeft} />}
        title={t('doctor_name')}
        text={appointment.doctorName}
      />

      <InfoBox
        iconLeft={<EventOutlinedIcon sx={styles.iconLeft} />}
        title={t('date_and_time')}
        text={appointment.date}
        subText={`às ${appointment.time}`}
      />
      <Box sx={styles.footer}>
        {isSmallScreen ? (
          <IconButton onClick={() => onViewDetails(appointment.id)}>
            <ChevronRightOutlinedIcon color="primary" sx={styles.chevronIcon} />
          </IconButton>
        ) : (
          <Typography
            component="a"
            onClick={() => onViewDetails(appointment.id)}
            sx={styles.link}
          >
            {t('see_details')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const styles = {
  card: {
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    position: 'relative',
    paddingBottom: '1rem',
  },
  header: {
    padding: 2,
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    display: 'flex',
    gap: 2,
  },
  headerTitle: {
    color: theme.palette.secondary.contrastText,
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  headerName: {
    color: theme.palette.secondary.contrastText,
    fontSize: '1.4rem',
  },
  iconLeft: {
    fontSize: '2rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    padding: 2,
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  link: {
    fontWeight: 500,
    fontSize: '1.4rem',
    textAlign: 'center',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    paddingInline: '1rem',
    paddingTop: 1,
    paddingBottom: 1,
    borderRadius: 6,
    width: '13.6rem',
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      cursor: 'pointer',
    },
  },
  chevronIcon: {
    fontSize: '2rem',
    marginLeft: '0.5rem',
    verticalAlign: 'middle',
    border: '1px solid red',
    borderRadius: '50%',
  },
};
